'use server'

import type { Payload } from 'payload'

import type { Cliente } from '@/payload-types'

import configPromise from '@payload-config'
import crypto from 'crypto'
import { cookies, headers } from 'next/headers'
import { redirect } from 'next/navigation'
import {
  APIError,
  AuthenticationError,
  createLocalReq,
  getPayload,
  LockedAuth,
  logoutOperation,
  UnverifiedEmail,
  ValidationError,
} from 'payload'

import { isAdminUser } from '@/access/isAdmin'
import { inviaEmail } from '@/negozio/email/layout'
import { emailVerifica } from './emailAuth'
import { erroriCampi, testo, type StatoForm } from './form'
import { getCliente } from './getCliente'
import { PASSWORD_MIN, VERSIONE_PRIVACY } from './regole'

export type { StatoForm } from './form'

/**
 * Server actions behind `/account`. They talk to the Local API and keep the session in the
 * same HTTP-only cookie Payload uses (`payload-token`), so `/api/clienti/me` and
 * `getCliente()` see the customer as logged in.
 */

const nomeCookie = (payload: Payload) => `${payload.config.cookiePrefix}-token`

const impostaSessione = async (payload: Payload, token: string) => {
  const auth = payload.collections.clienti.config.auth
  ;(await cookies()).set(nomeCookie(payload), token, {
    httpOnly: true,
    path: '/',
    sameSite: 'lax',
    secure: auth.cookies.secure,
    domain: auth.cookies.domain || undefined,
    maxAge: auth.tokenExpiration,
  })
}

/** Only same-site paths: `?next=//evil.com` must not turn login into an open redirect. */
const destinazioneSicura = (valore: string) =>
  valore.startsWith('/') && !valore.startsWith('//') && !valore.startsWith('/\\')
    ? valore
    : '/account'

export async function accedi(_prev: StatoForm, formData: FormData): Promise<StatoForm> {
  const email = testo(formData, 'email').toLowerCase()
  const password = String(formData.get('password') ?? '')
  const valori = { email }
  if (!email || !password) {
    return { stato: 'errore', messaggio: 'Inserisci email e password.', valori }
  }

  const payload = await getPayload({ config: configPromise })
  try {
    const { token } = await payload.login({ collection: 'clienti', data: { email, password } })
    if (!token) throw new Error('Login senza token')
    await impostaSessione(payload, token)
  } catch (err) {
    let messaggio = 'Accesso non riuscito. Riprova tra qualche minuto.'
    if (err instanceof UnverifiedEmail) {
      // Payload only throws this after the password matched, so offering the resend button
      // here reveals nothing to someone guessing emails.
      return {
        stato: 'errore',
        messaggio:
          'Devi prima confermare il tuo indirizzo email: apri il link che ti abbiamo inviato (controlla anche lo spam).',
        nonVerificato: true,
        valori,
      }
    } else if (err instanceof LockedAuth) {
      messaggio =
        'Troppi tentativi non riusciti: l’account è bloccato per 15 minuti. Puoi anche reimpostare la password.'
    } else if (err instanceof AuthenticationError) {
      messaggio = 'Email o password non corretti.'
    } else {
      payload.logger.error({ err }, 'Login cliente fallito')
    }
    return { stato: 'errore', messaggio, valori }
  }

  redirect(destinazioneSicura(testo(formData, 'next')))
}

/** The personal and company inputs shared by registration and the profile form. */
const leggiAnagrafica = (formData: FormData) => ({
  tipoCliente: (testo(formData, 'tipoCliente') === 'azienda'
    ? 'azienda'
    : 'privato') as Cliente['tipoCliente'],
  nome: testo(formData, 'nome'),
  cognome: testo(formData, 'cognome'),
  ragioneSociale: testo(formData, 'ragioneSociale'),
  partitaIva: testo(formData, 'partitaIva'),
  codiceFiscale: testo(formData, 'codiceFiscale'),
  pec: testo(formData, 'pec'),
  codiceSdi: testo(formData, 'codiceSdi'),
  telefono: testo(formData, 'telefono'),
})

/** Empty optional inputs → unset; the collection normalizes and validates the rest. */
const facoltativiANull = (valori: ReturnType<typeof leggiAnagrafica>) => ({
  ragioneSociale: valori.ragioneSociale || null,
  partitaIva: valori.partitaIva || null,
  codiceFiscale: valori.codiceFiscale || null,
  pec: valori.pec || null,
  codiceSdi: valori.codiceSdi || null,
  telefono: valori.telefono || null,
})

export async function registrati(_prev: StatoForm, formData: FormData): Promise<StatoForm> {
  const valori = { ...leggiAnagrafica(formData), email: testo(formData, 'email').toLowerCase() }
  const password = String(formData.get('password') ?? '')
  const privacy = formData.get('privacy') === 'on'
  const marketing = formData.get('marketing') === 'on'
  const collegaOrdini = formData.get('collegaOrdini') === 'on'
  // What the form gets back on error: the inputs plus the checkboxes.
  const ripristino = {
    ...valori,
    privacy: privacy ? 'on' : '',
    marketing: marketing ? 'on' : '',
    collegaOrdini: collegaOrdini ? 'on' : '',
  }

  const campi: Record<string, string> = {}
  if (password.length < PASSWORD_MIN) campi.password = `Usa almeno ${PASSWORD_MIN} caratteri.`
  if (!privacy) {
    campi.consensoPrivacy = 'Per creare l’account devi accettare l’informativa privacy.'
  }
  if (Object.keys(campi).length) {
    return {
      stato: 'errore',
      messaggio: 'Controlla i campi evidenziati.',
      campi,
      valori: ripristino,
    }
  }

  const payload = await getPayload({ config: configPromise })
  try {
    // `overrideAccess: false` with no user: exactly what an anonymous REST call could do.
    await payload.create({
      collection: 'clienti',
      overrideAccess: false,
      data: {
        ...valori,
        ...facoltativiANull(valori),
        password,
        consensoPrivacy: { data: new Date().toISOString(), versione: VERSIONE_PRIVACY },
        consensoMarketing: { accettato: marketing },
        // Done at the first login, once the email is proven (`collegaOrdiniOspite`).
        collegaOrdiniOspite: collegaOrdini,
      },
    })
  } catch (err) {
    if (err instanceof ValidationError) {
      return {
        stato: 'errore',
        messaggio: 'Controlla i campi evidenziati.',
        campi: erroriCampi(err),
        valori: ripristino,
      }
    }
    payload.logger.error({ err }, 'Registrazione cliente fallita')
    return {
      stato: 'errore',
      messaggio: 'Registrazione non riuscita. Riprova tra qualche minuto.',
      valori: ripristino,
    }
  }

  return {
    stato: 'ok',
    messaggio: `Ti abbiamo inviato un’email a ${valori.email}: apri il link per confermare l’indirizzo e attivare l’account.`,
  }
}

export async function esci(): Promise<void> {
  const payload = await getPayload({ config: configPromise })
  const { user } = await payload.auth({ headers: await headers() }).catch(() => ({ user: null }))

  // The cookie is shared with /admin: never log a staff member out from the shop.
  if (isAdminUser(user)) redirect('/account')

  if (user?.collection === 'clienti') {
    try {
      // Drops this session server-side, so a copied token stops working too.
      const req = await createLocalReq({ user }, payload)
      await logoutOperation({ collection: payload.collections.clienti, req })
    } catch (err) {
      payload.logger.error({ err }, 'Logout cliente fallito')
    }
  }
  ;(await cookies()).delete(nomeCookie(payload))
  redirect('/account?uscito=1')
}

export async function passwordDimenticata(
  _prev: StatoForm,
  formData: FormData,
): Promise<StatoForm> {
  const email = testo(formData, 'email').toLowerCase()
  if (!/^\S+@\S+\.\S+$/.test(email)) {
    return { stato: 'errore', campi: { email: 'Indirizzo email non valido.' }, valori: { email } }
  }

  const payload = await getPayload({ config: configPromise })
  try {
    await payload.forgotPassword({ collection: 'clienti', data: { email } })
  } catch (err) {
    payload.logger.error({ err }, 'Recupero password fallito')
  }
  // Same answer whether the account exists or not: the form must not reveal who is registered.
  return {
    stato: 'ok',
    messaggio: `Se ${email} corrisponde a un account, riceverai a breve un’email con il link per scegliere una nuova password. Il link vale un’ora.`,
  }
}

/** Minimum gap between two resends to the same address (per server process). */
const PAUSA_REINVIO_MS = 60 * 1000
const ultimiReinvii = new Map<string, number>()

/**
 * Payload has no Local API to resend the verification email: this regenerates
 * `_verificationToken` (the previous link stops working) and sends `emailVerifica` again.
 * Same answer whether or not the account exists or is already verified.
 */
export async function reinviaVerifica(_prev: StatoForm, formData: FormData): Promise<StatoForm> {
  const email = testo(formData, 'email').toLowerCase()
  if (!/^\S+@\S+\.\S+$/.test(email)) {
    return { stato: 'errore', messaggio: 'Indirizzo email non valido.', valori: { email } }
  }
  const risposta: StatoForm = {
    stato: 'ok',
    messaggio: `Se ${email} corrisponde a un account da confermare, riceverai a breve una nuova email con il link di conferma. I link inviati prima non valgono più.`,
  }

  const adesso = Date.now()
  if (adesso - (ultimiReinvii.get(email) ?? 0) < PAUSA_REINVIO_MS) return risposta
  ultimiReinvii.set(email, adesso)
  for (const [indirizzo, quando] of ultimiReinvii) {
    if (adesso - quando >= PAUSA_REINVIO_MS) ultimiReinvii.delete(indirizzo)
  }

  const payload = await getPayload({ config: configPromise })
  try {
    const {
      docs: [cliente],
    } = await payload.find({
      collection: 'clienti',
      where: { email: { equals: email } },
      limit: 1,
      depth: 0,
      overrideAccess: true,
      showHiddenFields: true,
    })
    if (!cliente || cliente._verified === true) return risposta

    // Same token format as Payload's `create`. Straight to the DB: `_verificationToken` is a
    // hidden auth field, and the cliente hooks have nothing to do here.
    const token = crypto.randomBytes(20).toString('hex')
    await payload.db.updateOne({
      collection: 'clienti',
      id: cliente.id,
      data: { _verificationToken: token },
    })
    await inviaEmail(payload, { to: cliente.email, ...emailVerifica(token, cliente) })
  } catch (err) {
    payload.logger.error({ err }, 'Reinvio email di conferma fallito')
  }
  return risposta
}

export async function reimpostaPassword(_prev: StatoForm, formData: FormData): Promise<StatoForm> {
  const token = testo(formData, 'token')
  const password = String(formData.get('password') ?? '')
  const conferma = String(formData.get('conferma') ?? '')

  if (!token) {
    return { stato: 'errore', messaggio: 'Link non valido: richiedine uno nuovo.' }
  }
  if (password.length < PASSWORD_MIN) {
    return { stato: 'errore', campi: { password: `Usa almeno ${PASSWORD_MIN} caratteri.` } }
  }
  if (password !== conferma) {
    return { stato: 'errore', campi: { conferma: 'Le due password non coincidono.' } }
  }

  const payload = await getPayload({ config: configPromise })
  try {
    const { token: sessione, user } = await payload.resetPassword({
      collection: 'clienti',
      data: { token, password },
      overrideAccess: true,
    })
    // The link arrived by email, which proves the address: an account that never confirmed
    // it would otherwise stay locked out right after choosing a new password.
    // Payload types the returned user loosely (`Record<string, unknown>`).
    const id = user?.id as Cliente['id'] | undefined
    if (id && user._verified !== true) {
      await payload.update({
        collection: 'clienti',
        id,
        data: { _verified: true },
        overrideAccess: true,
      })
    }
    if (sessione) await impostaSessione(payload, sessione)
  } catch (err) {
    payload.logger.warn({ err }, 'Reimpostazione password fallita')
    return {
      stato: 'errore',
      messaggio: 'Il link è scaduto o è già stato usato: richiedine uno nuovo.',
    }
  }

  redirect('/account?password=aggiornata')
}

/** The profile form and the account deletion need a logged-in cliente (never staff). */
const clienteOAccesso = async () => {
  const cliente = await getCliente()
  if (!cliente) redirect('/account')
  return cliente
}

export async function aggiornaProfilo(_prev: StatoForm, formData: FormData): Promise<StatoForm> {
  const cliente = await clienteOAccesso()
  const valori = leggiAnagrafica(formData)
  const marketing = formData.get('marketing') === 'on'
  const ripristino = { ...valori, marketing: marketing ? 'on' : '' }

  const payload = await getPayload({ config: configPromise })
  try {
    // As the cliente itself: the collection access and hooks decide what it may change
    // (not its email, not its privacy consent), exactly as for a REST call.
    await payload.update({
      collection: 'clienti',
      id: cliente.id,
      overrideAccess: false,
      user: cliente,
      data: {
        ...valori,
        ...facoltativiANull(valori),
        // `data` is kept while the consent stays given, stamped anew when it's given again.
        consensoMarketing: { accettato: marketing, data: cliente.consensoMarketing?.data ?? null },
      },
    })
  } catch (err) {
    const errore = (messaggio: string, campi?: Record<string, string>) => ({
      stato: 'errore' as const,
      messaggio,
      campi,
      valori: ripristino,
    })
    if (err instanceof ValidationError) {
      return errore('Controlla i campi evidenziati.', erroriCampi(err))
    }
    if (err instanceof APIError && err.isPublic) return errore(err.message)
    payload.logger.error({ err }, 'Aggiornamento profilo cliente fallito')
    return errore('Salvataggio non riuscito. Riprova tra qualche minuto.')
  }

  redirect('/account?profilo=aggiornato')
}

/**
 * Deletes the logged-in cliente's account, after re-checking its password and an explicit
 * confirmation. Its addresses go with it (`beforeDelete` on `Clienti`); its orders stay, as
 * guest orders with the email snapshot, because invoices and returns need them.
 */
export async function eliminaAccount(_prev: StatoForm, formData: FormData): Promise<StatoForm> {
  const cliente = await clienteOAccesso()
  const password = String(formData.get('password') ?? '')
  if (formData.get('conferma') !== 'on') {
    return {
      stato: 'errore',
      campi: { conferma: 'Spunta la casella per confermare l’eliminazione.' },
    }
  }
  if (!password) {
    return { stato: 'errore', campi: { password: 'Inserisci la tua password.' } }
  }

  const payload = await getPayload({ config: configPromise })
  try {
    // A borrowed or stolen session alone must not be enough to erase the account.
    await payload.login({ collection: 'clienti', data: { email: cliente.email, password } })
  } catch (err) {
    if (err instanceof LockedAuth) {
      return {
        stato: 'errore',
        messaggio: 'Troppi tentativi non riusciti: l’account è bloccato per 15 minuti.',
      }
    }
    if (!(err instanceof AuthenticationError)) {
      payload.logger.error({ err }, 'Verifica password per eliminazione account fallita')
    }
    return { stato: 'errore', campi: { password: 'Password non corretta.' } }
  }

  try {
    await payload.delete({
      collection: 'clienti',
      id: cliente.id,
      overrideAccess: false,
      user: cliente,
    })
  } catch (err) {
    payload.logger.error({ err }, 'Eliminazione account cliente fallita')
    return {
      stato: 'errore',
      messaggio: 'Eliminazione non riuscita. Riprova tra qualche minuto o scrivici.',
    }
  }

  // The sessions went with the document: only the cookie is left to drop.
  ;(await cookies()).delete(nomeCookie(payload))
  redirect('/account?eliminato=1')
}
